import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Download, X as CloseIcon } from '@phosphor-icons/react';

/**
 * Visionneuse PDF inline pour le portail staff.
 *
 * Affiche un PDF dans un Dialog plein écran (max 95vh × 95vw) via une
 * balise <object> avec fallback <iframe>. Sur mobile, le navigateur ouvre
 * son lecteur PDF natif intégré. Sur desktop, le PDF est rendu dans le
 * Dialog directement.
 *
 * Props:
 *   - open : boolean
 *   - onOpenChange : (open) => void
 *   - url : string (chemin /docs/XYZ.pdf)
 *   - title : string (titre affiché en header)
 */
const PdfViewerDialog = ({ open, onOpenChange, url, title }) => {
  if (!url) return null;

  // On force la consultation inline via le query param (certains serveurs
  // honorent ?inline=1 ; sinon le navigateur décide selon Content-Disposition)
  const inlineUrl = url + (url.includes('?') ? '&' : '?') + 'inline=1';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-5xl w-[95vw] h-[95vh] p-0 gap-0 flex flex-col overflow-hidden"
        data-testid="pdf-viewer-dialog"
      >
        <DialogHeader className="flex-row items-center justify-between gap-2 px-4 py-3 border-b bg-white">
          <DialogTitle className="text-base font-medium text-gray-900 truncate flex-1 min-w-0">
            {title || 'Document'}
          </DialogTitle>
          <div className="flex items-center gap-1 flex-shrink-0">
            <a
              href={url}
              download
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#2E7D32] text-white text-xs font-medium hover:bg-[#256628] transition-colors"
              data-testid="pdf-viewer-download-btn"
              aria-label="Télécharger le PDF"
            >
              <Download size={14} weight="bold" />
              <span className="hidden sm:inline">Télécharger</span>
            </a>
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              className="p-2 rounded-lg hover:bg-gray-100 text-gray-500 transition-colors"
              data-testid="pdf-viewer-close-btn"
              aria-label="Fermer"
            >
              <CloseIcon size={18} weight="bold" />
            </button>
          </div>
        </DialogHeader>
        <div className="flex-1 bg-gray-50 overflow-hidden">
          <object
            data={inlineUrl}
            type="application/pdf"
            className="w-full h-full"
            aria-label={title || 'Document PDF'}
          >
            {/* Fallback : iframe (certains anciens navigateurs préfèrent) */}
            <iframe
              src={inlineUrl}
              className="w-full h-full border-0"
              title={title || 'Document PDF'}
            />
            {/* Fallback ultime : message + lien */}
            <div className="flex flex-col items-center justify-center h-full text-center p-6">
              <p className="text-sm text-gray-600 mb-3">
                Votre navigateur ne peut pas afficher ce PDF directement.
              </p>
              <a
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#2E7D32] text-white text-sm font-medium hover:bg-[#256628]"
              >
                <Download size={16} weight="bold" />
                Ouvrir / télécharger le PDF
              </a>
            </div>
          </object>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PdfViewerDialog;
